import { AnimateOnScroll } from "@/components/AnimateOnScroll";
import { useCountUp } from "@/hooks/useCountUp";
import { LucideIcon } from "lucide-react";

interface Stat {
  label: string;
  value: number;
  suffix: string;
  icon: LucideIcon;
}

interface StatsSectionProps {
  stats: Stat[];
  gradient: string; // e.g., "from-blue-500 to-indigo-500"
}

export const StatsSection = ({ stats, gradient }: StatsSectionProps) => {
  return (
    <section className="py-8 sm:py-12 md:py-16 bg-background relative -mt-8 sm:-mt-12 md:-mt-16 z-20">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {stats.map((stat, index) => (
            <StatCard key={index} stat={stat} index={index} gradient={gradient} />
          ))}
        </div>
      </div>
    </section>
  );
};

const StatCard = ({ stat, index, gradient }: { stat: Stat; index: number; gradient: string }) => {
  const { count, ref } = useCountUp(stat.value, 2000);
  const Icon = stat.icon;

  return (
    <AnimateOnScroll animation="fade-up" delay={index * 100}>
      <div
        ref={ref}
        className="bg-card rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 shadow-lg sm:shadow-xl border border-border hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
      >
        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
        </div>
        <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-0.5 sm:mb-1">
          {count}{stat.suffix}
        </div>
        <div className="text-muted-foreground text-xs sm:text-sm">{stat.label}</div>
      </div>
    </AnimateOnScroll>
  );
};
